import { useState, useEffect, useRef } from 'react'
import { toast } from 'react-hot-toast'
import { api } from '../../utils/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import { AvatarHelper } from '../../utils/avatarHelper'
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  CheckIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  ChevronRightIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline'

// ... (保留 User 接口)
interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'student' | 'teacher' | 'admin'
  avatar?: string
  phone?: string
  status: 'active' | 'inactive' | 'pending'
  emailVerified: boolean
  phoneVerified: boolean
  createdAt: string
  updatedAt: string
  teacher?: {
    id: string
    title: string
    status: string
  }
  // 模拟的额外数据
  loginCount?: number
  lastLogin?: string
  totalSpent?: number
}

const AdminUsers = () => {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<'all' | 'student' | 'teacher' | 'admin'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingUser, setDeletingUser] = useState<User | null>(null)
  
  // 消息功能状态
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [messagingUser, setMessagingUser] = useState<User | null>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  
  // 批量操作状态
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // ... (保留 fetchUsers, useEffects, handleDeleteUser 等核心逻辑)
  const fetchUsers = async () => {
    try {
      setLoading(true)
      const data = await api.get<User[]>('/admin/users')
      // 使用真实数据，如果后端返回了额外字段则保留
      const enrichedData = data.map(u => ({
          ...u,
          loginCount: u.loginCount ?? 0,
          lastLogin: u.lastLogin || u.updatedAt || u.createdAt,
          totalSpent: u.totalSpent ?? 0
      }))
      setUsers(enrichedData)
      setFilteredUsers(enrichedData)
    } catch (error) {
      console.error('获取用户列表失败:', error)
      toast.error('获取用户列表失败')
      setUsers([])
      setFilteredUsers([])
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (user: User) => {
    // ... (保留原有逻辑)
    try {
      setDeletingUser(user)
      await api.delete(`/admin/users/${user.id}`)
      toast.success('用户删除成功')
      fetchUsers()
      setShowDeleteModal(false)
      setDeletingUser(null)
    } catch (error) {
      console.error('删除用户失败:', error)
      toast.error('删除用户失败')
      setDeletingUser(null)
    }
  }

  // 打开消息弹窗
  const openMessageModal = (user: User) => {
    setMessagingUser(user)
    setShowMessageModal(true)
    fetchMessages(user.id)
  }

  // 获取与用户的消息
  const fetchMessages = async (userId: string) => {
    setLoadingMessages(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/messages/conversation/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setMessages(data.messages || [])
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
      }
    } catch (error) {
      console.error('获取消息失败:', error)
    } finally {
      setLoadingMessages(false)
    }
  }

  // 发送消息
  const sendMessage = async () => {
    if (!newMessage.trim() || !messagingUser || sendingMessage) return

    setSendingMessage(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          receiverId: messagingUser.id,
          content: newMessage.trim(),
          type: 'text'
        })
      })

      if (response.ok) {
        const data = await response.json()
        setMessages(prev => [...prev, data.data])
        setNewMessage('')
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
        toast.success('消息发送成功')
      } else {
        toast.error('发送失败')
      }
    } catch (error) {
      console.error('发送消息失败:', error)
      toast.error('发送消息失败')
    } finally {
      setSendingMessage(false)
    }
  }

  const handleMessageKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const currentUserId = localStorage.getItem('userId')

  useEffect(() => { fetchUsers() }, [])

  useEffect(() => {
    let filtered = [...users]
    if (searchTerm) {
      filtered = filtered.filter(user => 
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    if (roleFilter !== 'all') filtered = filtered.filter(user => user.role === roleFilter)
    if (statusFilter !== 'all') filtered = filtered.filter(user => user.status === statusFilter)
    setFilteredUsers(filtered)
  }, [users, searchTerm, roleFilter, statusFilter])

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUsers.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredUsers.map(u => u.id)))
    }
  }

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedIds)
    if (newSet.has(id)) newSet.delete(id)
    else newSet.add(id)
    setSelectedIds(newSet)
  }

  const getRoleBadge = (role: string) => {
    const styles = {
      student: 'bg-blue-100 text-blue-800',
      teacher: 'bg-purple-100 text-purple-800',
      admin: 'bg-gray-800 text-white'
    }
    const labels = { student: '学生', teacher: '教师', admin: '管理员' }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[role as keyof typeof styles]}`}>
        {labels[role as keyof typeof labels]}
      </span>
    )
  }

  if (loading) return <div className="flex justify-center h-96 items-center"><LoadingSpinner size="lg" /></div>

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 relative">
      {/* 头部操作区 */}
      <div className="md:flex md:items-center md:justify-between mb-6 gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">用户管理</h1>
          <p className="mt-1 text-sm text-gray-500">
            共 {filteredUsers.length} 位用户，{selectedIds.size > 0 ? `已选中 ${selectedIds.size} 位` : '您可以进行批量管理'}
          </p>
        </div>
        <div className="flex gap-3">
          {selectedIds.size > 0 && (
            <div className="flex gap-2 animate-fade-in">
              <button className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-sm font-medium border border-red-200 transition-colors">
                批量删除
              </button>
              <button className="px-4 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 text-sm font-medium border border-gray-200 transition-colors">
                批量停用
              </button>
            </div>
          )}
          <button className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-sm text-sm font-medium transition-all">
            <UserPlusIcon className="h-4 w-4 mr-2" />
            添加用户
          </button>
        </div>
      </div>

      {/* 搜索筛选栏 */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索姓名、邮箱、手机号..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <select 
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-indigo-500"
            >
              <option value="all">所有角色</option>
              <option value="student">学生</option>
              <option value="teacher">教师</option>
              <option value="admin">管理员</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-indigo-500"
            >
              <option value="all">所有状态</option>
              <option value="active">活跃</option>
              <option value="inactive">停用</option>
              <option value="pending">待审核</option>
            </select>
          </div>
        </div>
      </div>

      {/* 用户表格 */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  checked={selectedIds.size === filteredUsers.length && filteredUsers.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">角色</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">最近登录</th>
              <th className="relative px-6 py-3"><span className="sr-only">操作</span></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr 
                key={user.id} 
                className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedUser?.id === user.id ? 'bg-indigo-50' : ''}`}
                onClick={() => setSelectedUser(user)}
              >
                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    checked={selectedIds.has(user.id)}
                    onChange={() => toggleSelect(user.id)}
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                        {user.avatar ? (
                          <img className="h-10 w-10 rounded-full" src={AvatarHelper.getAvatarUrl(user.avatar)} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                            {user.firstName[0]}
                          </div>
                        )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getRoleBadge(user.role)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' : 
                    user.status === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {user.status === 'active' ? '活跃' : user.status === 'inactive' ? '停用' : '待审核'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(user.lastLogin || user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-gray-400 hover:text-gray-600">
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 用户详情抽屉 (Drawer) */}
      {selectedUser && (
        <>
          <div 
            className="fixed inset-0 bg-black/30 z-40 transition-opacity" 
            onClick={() => setSelectedUser(null)}
          ></div>
          <div className="fixed inset-y-0 right-0 w-full md:w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-8">
                <h2 className="text-xl font-bold text-gray-900">用户详情</h2>
                <button 
                  onClick={() => setSelectedUser(null)}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="text-center mb-8">
                <div className="w-24 h-24 rounded-full bg-gray-100 mx-auto mb-4 flex items-center justify-center text-3xl text-gray-400 overflow-hidden">
                   {selectedUser.avatar ? (
                      <img src={AvatarHelper.getAvatarUrl(selectedUser.avatar)} className="w-full h-full object-cover" alt="" />
                   ) : (
                      <UserIcon className="w-12 h-12" />
                   )}
                </div>
                <h3 className="text-xl font-bold text-gray-900">{selectedUser.firstName} {selectedUser.lastName}</h3>
                <p className="text-gray-500 text-sm mt-1">ID: {selectedUser.id}</p>
                <div className="mt-4 flex justify-center gap-3">
                  <button 
                    onClick={() => openMessageModal(selectedUser)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"
                  >
                    <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    发送消息
                  </button>
                  <button 
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                    onClick={() => {
                        setDeletingUser(selectedUser)
                        setShowDeleteModal(true)
                    }}
                  >
                    删除账户
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">基本信息</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-500 flex items-center"><EnvelopeIcon className="w-4 h-4 mr-2"/> 邮箱</span>
                      <span className="text-gray-900 font-medium">{selectedUser.email}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-500 flex items-center"><PhoneIcon className="w-4 h-4 mr-2"/> 电话</span>
                      <span className="text-gray-900 font-medium">{selectedUser.phone || '-'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-500 flex items-center"><CalendarIcon className="w-4 h-4 mr-2"/> 注册时间</span>
                      <span className="text-gray-900 font-medium">{new Date(selectedUser.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500 flex items-center"><ShieldCheckIcon className="w-4 h-4 mr-2"/> 验证状态</span>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${selectedUser.emailVerified ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {selectedUser.emailVerified ? '已验证' : '未验证'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">活动统计</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-center">
                      <p className="text-xs text-gray-500 mb-1">登录次数</p>
                      <p className="text-xl font-bold text-indigo-600">{selectedUser.loginCount}</p>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 text-center">
                      <p className="text-xs text-gray-500 mb-1">总消费</p>
                      <p className="text-xl font-bold text-green-600">¥{selectedUser.totalSpent}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 删除确认模态框 (保留原有逻辑) */}
      {showDeleteModal && deletingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
           <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full mx-4">
             <h3 className="text-lg font-bold text-gray-900 mb-2">确认删除用户?</h3>
             <p className="text-sm text-gray-500 mb-6">
               这将永久删除 {deletingUser.firstName} {deletingUser.lastName} 的所有数据，且无法恢复。
             </p>
             <div className="flex gap-3">
               <button 
                 onClick={() => setShowDeleteModal(false)}
                 className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
               >
                 取消
               </button>
               <button 
                 onClick={() => handleDeleteUser(deletingUser)}
                 className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
               >
                 确认删除
               </button>
             </div>
           </div>
        </div>
      )}

      {/* 消息弹窗 */}
      {showMessageModal && messagingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full h-[80vh] max-h-[600px] flex flex-col overflow-hidden">
            {/* 头部 */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  {messagingUser.avatar ? (
                    <img src={AvatarHelper.getAvatarUrl(messagingUser.avatar)} className="w-full h-full rounded-full object-cover" alt="" />
                  ) : (
                    <span className="text-lg font-bold">
                      {messagingUser.firstName?.[0]}{messagingUser.lastName?.[0]}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold">{messagingUser.firstName} {messagingUser.lastName}</h3>
                  <p className="text-sm text-white/80">{messagingUser.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowMessageModal(false)
                  setMessagingUser(null)
                  setMessages([])
                  setNewMessage('')
                }}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* 消息列表 */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 opacity-50" />
                  <p className="text-sm">暂无消息</p>
                  <p className="text-xs mt-1">发送消息开始与用户交流</p>
                </div>
              ) : (
                messages.map((msg: any) => {
                  const isMe = msg.senderId === currentUserId
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                          isMe
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                            : 'bg-white border border-gray-200 text-gray-700'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isMe ? 'text-white/60' : 'text-gray-400'}`}>
                          {new Date(msg.createdAt).toLocaleTimeString('zh-CN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {msg.status === 'read' && isMe && (
                            <span className="ml-1">✓✓</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* 输入框 */}
            <div className="p-4 bg-white border-t border-gray-100">
              <div className="flex items-end gap-2">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleMessageKeyDown}
                  placeholder="输入消息... (Shift+Enter换行)"
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-2xl text-sm resize-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
                  rows={1}
                  style={{ minHeight: '44px', maxHeight: '120px' }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="w-11 h-11 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-lg shadow-indigo-200/50 hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingMessage ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <PaperAirplaneIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUsers
